'use strict';

const libWorkspace = require('pict-section-workspace');
const libDocumentLookup = require('../documents/Document-Lookup.js');
const libUserSearch = require('../access/User-Search.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

/**
 * What the admin is told when a team change fails. The server's own text (e.g. "UNAUTHORIZED ACCESS IS NOT ALLOWED",
 * or a JSON body) is never shown.
 */
const WRITE_FAILURES =
{
	'add-member': 'That person could not be added to the team.',
	'set-role': 'The member\'s role could not be changed.',
	'remove-member': 'That person could not be removed from the team.',
	'grant': 'The document could not be granted to the team.',
	'revoke': 'The team\'s access to that document could not be removed.',
	'add-lab': 'The lab could not be added to the team.',
	'remove-lab': 'The lab could not be removed from the team.',
};

/**
 * @param {string} pAction - A WRITE_FAILURES key.
 * @param {any} pError - The failure (an Error, a message, or a response body).
 * @returns {string} One or two sentences for the admin: what failed and what to do.
 */
function writeFailureMessage(pAction, pError)
{
	const tmpWhat = WRITE_FAILURES[pAction] || 'The change could not be saved.';
	const tmpDetail = String((pError && (pError.message || pError.Error)) || pError || '');
	if (/already staffs/i.test(tmpDetail))
	{
		return `${tmpWhat} The team is already linked to that lab.`;
	}
	if (/unauthori[sz]ed|forbidden|not allowed|permission|\b40[135]\b/i.test(tmpDetail))
	{
		return `${tmpWhat} You don't have permission to change this team. Ask an administrator for the "Manage teams and document grants" access.`;
	}
	if (/timed out|ETIMEDOUT/i.test(tmpDetail))
	{
		return 'The server took too long to answer, so the change may have been saved. The list shows what the team holds now; check it before trying again.';
	}
	return `${tmpWhat} Check your connection and try again.`;
}

/**
 * TeamWorkspaceProvider — the engine behind the Team workspace (#/Team/Workspace/:ID[/:Tab], bridged over
 * /PSRS/Team/View/:GUID). Built on the finished server-side access engine (nothing new server-side is required):
 *   • Members & Roles — enroll a User in the team with a role (TeamUserJoin {IDTeam,IDUser,IDRole}); IDRole 0 = plain
 *                       member. Add / change role / remove.
 *   • Documents       — grant Documents to the team (DocumentAccessJoin {IDTeam,IDDocument,DocumentType,IDRole,
 *                       AccessKind,GrantRule}). The write projects into PermDocumentUser and PermProjectUser, so members
 *                       reach the document (and its project) with no project membership. Grant / revoke. A document is
 *                       found by number or by name within a project ([[Document-Lookup.js]]), never by a customer-wide scan.
 *   • Projects        — DERIVED read-only: the distinct projects of the team's granted documents (Steven's decision:
 *                       team→project is expressed as document grants, not a direct join). Granting a document here adds
 *                       the project automatically.
 *   • Sub-teams       — child Teams (ParentIDTeam); the access projection does not expand them (a grant reaches the
 *                       team's direct members only — docs/architecture/teams/05-gaps-and-direction.md).
 *   • Labs            — LabTeamJoin (which labs the team staffs). The API grants the team every test form whose tests
 *                       are assigned to that lab, and withdraws the grant when the lab is removed. A child lab's tests
 *                       are not included: link the team to each lab it works
 *                       (api/headlight/ts_source/bundles/contentmanagement/DocumentAccess-LabTeams.ts).
 * Config in [[Team.config.js]]. Inspection assignment (Team↔Inspection) is a later phase (needs a server InspectionTeamJoin).
 */
class TeamWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._team = null;
		this._parentName = '';
		this._tabs = {};            // per-tab {Loading, Loaded, Error, Rows, ...}
		this._names = { user: {}, login: {}, project: {}, lab: {}, role: {}, formType: {} };  // id/hash -> display caches
		// pending picker selections (the add-bars use searchable pickers, not native selects — there are 1,552 Roles)
		this._pendingUser = null; this._pendingRole = 0;
		this._pendingProject = 0; this._pendingDoc = null; this._pendingDocRole = 0;
		this._labAddPending = false;
		this._editMemberJoin = 0;   // the TeamUserJoin whose role is being edited inline
		/** @type {{ Kind: 'add' } | { Kind: 'remove', IDJoin: number } | null} the member add or removal waiting on the server */
		this._memberWrite = null;
		/** @type {{ Tab: string, Message: string } | null} the last failed change, shown on its tab until the next change */
		this._notice = null;
	}

	get idTeam() { return this._team ? this._team.IDTeam : null; }
	get idCustomer() { return (this._team && this._team.IDCustomer) || (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0; }
	get providerName() { return (this.options && this.options.ProviderIdentifier) || 'TeamWorkspace'; }

	// --- REST helpers ---
	_get(pURL) { return this._getJSON(pURL); }
	_postJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pPayload, (e, b) => e ? reject(e) : resolve(b))); }
	_putJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pPayload, (e, b) => e ? reject(e) : resolve(b))); }
	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			const m = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (m) { this.restClient.deleteEntity(m[1], m[2], (e, b) => e ? reject(e) : resolve(b)); return; }
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((r) => { if (!r.ok) { reject(new Error(`HTTP ${r.status}`)); return; } r.text().then((t) => resolve(t ? JSON.parse(t) : {})).catch(() => resolve({})); })
				.catch(reject);
		});
	}
	_fmtDate(pV) { if (!pV) { return ''; } try { const d = new Date(pV); if (!Number.isNaN(d.getTime())) { return d.toLocaleDateString(); } } catch (e) { /* */ } return String(pV).slice(0, 10); }

	// Roles are huge (1,552 rows incl. permission-groups + user-groups) — never preload; resolve names by id, and select
	// via a searchable picker filtered to real roles (Type 'Role' or legacy '', excluding PermissionGroup/UserGroup).
	async _resolveRoleNames(pIDs)
	{
		const ids = [ ...new Set(pIDs.map(Number).filter((x) => x && !this._names.role[x])) ];
		if (!ids.length) { return; }
		const rows = await this._get(`Roles/FilteredTo/FBL~IDRole~INN~${ids.join(',')}/0/${ids.length}`);
		(Array.isArray(rows) ? rows : []).forEach((r) => { if (r.IDRole) { this._names.role[r.IDRole] = r.Name || `Role ${r.IDRole}`; } });
	}
	_roleLabel(pID) { const id = Number(pID) || 0; if (!id) { return '<span class="tw-muted">member</span>'; } return this._names.role[id] ? this._esc(this._names.role[id]) : `Role #${id}`; }
	_roleDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'Roles', IDField: 'IDRole', Scope: 'FBV~Type~NE~PermissionGroup~FBV~Type~NE~UserGroup~FBV~Deleted~EQ~0',
				ToOption: (r) => ({ Value: Number(r.IDRole), Text: r.Name || `Role ${r.IDRole}`, Record: r }),
			});
	}

	/**
	 * The picker option for a role, so a picker opened on a role already chosen shows its name.
	 * @param {any} pIDRole
	 * @returns {Promise<{ Value: number, Text: string } | null>} null for no role (a plain member).
	 */
	async _roleOption(pIDRole)
	{
		const tmpID = Number(pIDRole) || 0;
		if (!tmpID)
		{
			return null;
		}
		try
		{
			await this._resolveRoleNames([ tmpID ]);
		}
		catch (pError)
		{
			// the name stays unresolved; the option falls back to the number
		}
		return { Value: tmpID, Text: this._names.role[tmpID] || `Role #${tmpID}` };
	}
	/**
	 * @returns {number} The role (0 = plain member) of the member whose role is being edited.
	 */
	_editingMemberRole()
	{
		const tmpRow = (this._state('members').Rows || []).find((r) => r.IDTeamUserJoin === this._editMemberJoin);
		return (tmpRow && Number(tmpRow.IDRole)) || 0;
	}

	// --- hydrate: base loaded the scalar Team; resolve parent name + roles ---
	async hydrateAsync(pRecord)
	{
		this._tabs = {};
		const tmpFull = await this._get(`Team/${pRecord.IDTeam}`);
		this._team = (tmpFull && !tmpFull.Error) ? tmpFull : pRecord;
		this._parentName = '';
		if (this._team.ParentIDTeam)
		{
			const p = await this._get(`Team/${Number(this._team.ParentIDTeam)}`);
			if (p && p.IDTeam) { this._parentName = p.Name || `Team ${p.IDTeam}`; }
		}
	}

	// --- per-tab load + repaint (mirrors the Observation workspace) ---
	async _ensureTabDataAsync(pTab, pForce)
	{
		if (!this._team) { return; }
		if (!pForce && this._tabs[pTab] && this._tabs[pTab].Loaded) { this._scheduleMount(pTab); return; }
		this._tabs[pTab] = { Loading: true, Loaded: false };
		this.repaintBody();
		try
		{
			if (pTab === 'overview') { await this._loadOverview(); }
			else if (pTab === 'members') { this._tabs[pTab].Rows = await this._loadMembers(); }
			else if (pTab === 'documents') { this._tabs[pTab].Rows = await this._loadDocuments(); }
			else if (pTab === 'projects') { this._tabs[pTab].Rows = await this._loadProjects(); }
			else if (pTab === 'inspections') { this._tabs[pTab].Rows = await this._loadInspections(); }
			else if (pTab === 'subteams') { this._tabs[pTab].Rows = await this._loadSubteams(); }
			else if (pTab === 'labs') { this._tabs[pTab].Rows = await this._loadLabs(); }
			this._tabs[pTab].Loaded = true;
		}
		catch (e) { this._tabs[pTab].Error = (e && e.message) || String(e); this._tabs[pTab].Loaded = true; }
		finally { this._tabs[pTab].Loading = false; this.repaintBody(); this._scheduleMount(pTab); }
	}
	_reload(pTab) { if (this._tabs[pTab]) { this._tabs[pTab].Loaded = false; } this._ensureTabDataAsync(pTab, true); }
	_state(pTab) { return this._tabs[pTab] || {}; }
	_loadingOrError(pTab)
	{
		const t = this._state(pTab);
		if (t.Loading || (!t.Loaded && !t.Error)) { return `<div class="tw-note"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>`; }
		if (t.Error) { return `<div class="tw-error">${this._esc(t.Error)}</div>`; }
		return null;
	}

	async _resolveNames(pKind, pIDs, pURL, pLabel)
	{
		const tmpMissing = [ ...new Set(pIDs.map(Number).filter((x) => x && !this._names[pKind][x])) ];
		if (!tmpMissing.length) { return; }
		const rows = await this._get(pURL(tmpMissing));
		(Array.isArray(rows) ? rows : []).forEach((r) => { const id = pLabel.id(r); if (id) { this._names[pKind][id] = pLabel.name(r); } });
	}

	// --- data loaders ---
	/**
	 * Read every row of a team list, 250 at a time (the API's page limit is 499). A failed read throws, so the tab says
	 * it could not load rather than showing an empty list (which would read as "nothing granted").
	 * @param {string} pFilteredURL - `<Entities>/FilteredTo/<filter>`, without begin/cap.
	 * @param {string} pWhat - What the rows are, for the message ("members", "documents", …).
	 * @returns {Promise<Array<Record<string, any>>>}
	 */
	async _getRows(pFilteredURL, pWhat)
	{
		const tmpPageSize = 250;
		const tmpRows = [];
		for (let tmpStart = 0; tmpStart < (tmpPageSize * 20); tmpStart += tmpPageSize)
		{
			const tmpPage = await this._get(`${pFilteredURL}/${tmpStart}/${tmpPageSize}`);
			if (!Array.isArray(tmpPage))
			{
				throw new Error(`The team's ${pWhat} could not be loaded. Check your connection and open the tab again.`);
			}
			tmpRows.push(...tmpPage);
			if (tmpPage.length < tmpPageSize)
			{
				break;
			}
		}
		return tmpRows;
	}
	async _loadMembers()
	{
		const list = await this._getRows(`TeamUserJoins/FilteredTo/FBV~IDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`, 'members');
		await this._resolveNames('user', list.map((r) => r.IDUser), (ids) => `Users/FilteredTo/FBL~IDUser~INN~${ids.join(',')}/0/${ids.length}`,
			{
				id: (u) => u.IDUser,
				name: (u) =>
				{
					// The login ID (or email) tells apart two members with the same name.
					this._names.login[u.IDUser] = u.LoginID || u.Email || '';
					return [ u.NameFirst, u.NameLast ].filter(Boolean).join(' ') || u.LoginID || `#${u.IDUser}`;
				},
			});
		await this._resolveRoleNames(list.map((r) => r.IDRole));
		return list;
	}
	async _loadDocuments()
	{
		const list = await this._getRows(`DocumentAccessJoins/FilteredTo/FBV~IDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`, 'documents');
		this._docMeta = this._docMeta || {};
		// LiteExtended: name, project and type only. A full Document read would carry each document's FormData.
		const docs = await this._innFetch('Documents/LiteExtended/Name,DocumentType', 'IDDocument', list.map((r) => r.IDDocument));
		docs.forEach((d) =>
		{
			this._docMeta[d.IDDocument] = { Name: d.Name || `Document #${d.IDDocument}`, IDProject: d.IDProject, DocumentType: d.DocumentType };
		});
		await this._resolveFormTypeNames(list.map((r) => r.DocumentType || ((this._docMeta[r.IDDocument] || {}).DocumentType)));
		await this._resolveRoleNames(list.map((r) => r.IDRole));
		return list;
	}
	/**
	 * Cache the form names of document type hashes (LADOTD-ATM → "LADOTD Audit of Testing and Materials").
	 * @param {Array<string>} pTypeHashes
	 */
	async _resolveFormTypeNames(pTypeHashes)
	{
		const tmpMissing = (pTypeHashes || []).filter((pHash) => pHash && !this._names.formType[pHash]);
		const tmpURL = libDocumentLookup.buildFormTypeNamesURL(tmpMissing, this.idCustomer);
		if (!tmpURL)
		{
			return;
		}
		Object.assign(this._names.formType, libDocumentLookup.formTypeNamesByHash(await this._get(tmpURL), this.idCustomer));
	}
	/**
	 * @param {string} pTypeHash - A DocumentType.
	 * @returns {string} The form name, or a neutral placeholder when the type is unknown (never the raw hash).
	 */
	_formTypeLabel(pTypeHash)
	{
		if (!pTypeHash)
		{
			return '—';
		}
		return this._names.formType[pTypeHash] || 'Unnamed form type';
	}
	async _loadProjects()
	{
		// Derived from the team's document grants: distinct projects of the granted documents.
		const grants = await this._loadDocuments();
		const byProject = {};
		grants.forEach((g) => { const meta = (this._docMeta || {})[g.IDDocument]; const pid = meta && meta.IDProject; if (pid) { byProject[pid] = (byProject[pid] || 0) + 1; } });
		const pids = Object.keys(byProject).map(Number);
		await this._resolveNames('project', pids, (ids) => `Projects/Lite/FilteredTo/FBL~IDProject~INN~${ids.join(',')}/0/${ids.length}`, { id: (p) => p.IDProject, name: (p) => p.Value || p.Name || `Project ${p.IDProject}` });
		return pids.map((pid) => ({ IDProject: pid, DocCount: byProject[pid] })).sort((a, b) => b.DocCount - a.DocCount);
	}
	// Chunked INN fetch (Meadow filter-length safety) — concatenates FBL~<field>~INN pages of 100 ids.
	async _innFetch(pEntity, pField, pIDs, pExtra)
	{
		const ids = [ ...new Set(pIDs.map(Number).filter(Boolean)) ]; const out = [];
		for (let i = 0; i < ids.length; i += 100)
		{
			const chunk = ids.slice(i, i + 100);
			const rows = await this._get(`${pEntity}/FilteredTo/FBL~${pField}~INN~${chunk.join(',')}${pExtra || ''}/0/${chunk.length}`);
			if (Array.isArray(rows)) { out.push(...rows); }
		}
		return out;
	}
	// Derived, read-only: the inspections this team can reach. A team is granted an inspection's document packet
	// (DocumentAccessJoin per doc) → those docs belong to inspections (InspectionDocumentJoin) → distinct inspections.
	// This is the "team sees its upcoming / in-progress / completed inspections" list, with NO InspectionTeamJoin.
	async _loadInspections()
	{
		const grants = await this._getRows(`DocumentAccessJoins/FilteredTo/FBV~IDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`, 'documents');
		const docIDs = [ ...new Set(grants.map((g) => g.IDDocument).filter(Boolean)) ];
		if (!docIDs.length) { return []; }
		const idj = await this._innFetch('InspectionDocumentJoins', 'IDDocument', docIDs, '~FBV~Deleted~EQ~0');
		const inspIDs = [ ...new Set(idj.map((r) => r.IDInspection).filter(Boolean)) ];
		if (!inspIDs.length) { return []; }
		const insp = await this._innFetch('Inspections', 'IDInspection', inspIDs, '~FBV~Deleted~EQ~0');
		return insp;
	}
	_inspectionBucket(pInsp)
	{
		const s = String(pInsp.StatusDisplay || '').toLowerCase();
		if (s.indexOf('complete') >= 0 || s.indexOf('closed') >= 0 || pInsp.EndDate) { return 'completed'; }
		if (pInsp.StartDate || s.indexOf('progress') >= 0 || s.indexOf('active') >= 0 || s.indexOf('field') >= 0) { return 'inprogress'; }
		return 'upcoming';
	}
	async _loadSubteams()
	{
		return this._getRows(`Teams/FilteredTo/FBV~ParentIDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0~FSF~Name~ASC~0`, 'sub-teams');
	}
	async _loadLabs()
	{
		const list = await this._getRows(`LabTeamJoins/FilteredTo/FBV~IDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`, 'labs');
		await this._resolveNames('lab', list.map((r) => r.IDLab), (ids) => `Labs/FilteredTo/FBL~IDLab~INN~${ids.join(',')}/0/${ids.length}`, { id: (l) => l.IDLab, name: (l) => l.Name || `Lab ${l.IDLab}` });
		await this._resolveRoleNames(list.map((r) => r.IDRole));
		return list;
	}
	async _loadOverview()
	{
		const [ members, docs, subs ] = await Promise.all([
			this._get(`TeamUserJoins/Count/FilteredTo/FBV~IDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`),
			this._get(`DocumentAccessJoins/Count/FilteredTo/FBV~IDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`),
			this._get(`Teams/Count/FilteredTo/FBV~ParentIDTeam~EQ~${this.idTeam}~FBV~Deleted~EQ~0`),
		]);
		this._tabs.overview = Object.assign(this._tabs.overview || {}, {
			Members: (members && members.Count) || 0,
			Documents: (docs && docs.Count) || 0,
			Subteams: (subs && subs.Count) || 0,
		});
	}

	// =========================================================== interactions ===========================================================
	/**
	 * Show a message on a tab until the next change. Repaints the tab (and re-mounts its pickers).
	 * @param {string} pTab
	 * @param {string} pMessage
	 */
	_showNotice(pTab, pMessage)
	{
		this._notice = { Tab: pTab, Message: pMessage };
		this.repaintBody();
		this._scheduleMount(pTab);
	}
	/**
	 * Run one team change: clear the last notice, write, and on failure tell the admin (the console gets the detail).
	 * The tab reloads either way, so it shows what the server holds.
	 * @param {string} pTab - The tab the change belongs to.
	 * @param {string} pAction - A WRITE_FAILURES key.
	 * @param {() => Promise<any>} fWrite
	 * @returns {Promise<boolean>} Whether the change was saved.
	 */
	async _write(pTab, pAction, fWrite)
	{
		this._notice = null;
		let tmpSaved = true;
		try
		{
			await fWrite();
		}
		catch (pError)
		{
			tmpSaved = false;
			this.pict.log.error(`Team ${this.idTeam} ${pAction} failed: ${(pError && pError.message) || pError}`);
			this._notice = { Tab: pTab, Message: writeFailureMessage(pAction, pError) };
		}
		this._reload(pTab);
		return tmpSaved;
	}
	/**
	 * Empty a picker after its pick has been used, so the next add starts blank.
	 * @param {string} pName - The picker's name (MemberPicker, DocPicker, …).
	 */
	_clearPicker(pName)
	{
		const tmpView = this.pict.views && this.pict.views[`${this.providerName}-${pName}`];
		if (tmpView && (typeof tmpView.clearValue === 'function'))
		{
			tmpView.clearValue();
		}
	}
	// Add-a-member is a two-picker + button flow (person + optional role, both searchable); commit reads the pending picks.
	async commitAddMember()
	{
		if (this._memberWrite)
		{
			return;
		}
		const sel = this._pendingUser;
		if (!sel || !sel.id)
		{
			this._showNotice('members', 'Pick a person to add to the team.');
			return;
		}
		const role = this._pendingRole || 0;
		this._clearPicker('MemberPicker');
		this._clearPicker('MemberRolePicker');
		this._pendingUser = null; this._pendingRole = 0;
		await this._runMemberWrite({ Kind: 'add' }, () => this.addMember(sel.id, role));
	}
	/**
	 * Run one member add or removal, showing it as in progress and refusing another until the server answers, so a
	 * second click can't send a duplicate.
	 * @param {{ Kind: 'add' } | { Kind: 'remove', IDJoin: number }} pWrite
	 * @param {() => Promise<any>} fWrite
	 */
	async _runMemberWrite(pWrite, fWrite)
	{
		this._memberWrite = pWrite;
		this.repaintBody();
		this._scheduleMount('members');
		try
		{
			await fWrite();
		}
		finally
		{
			this._memberWrite = null;
			if (!this._state('members').Loading)
			{
				this.repaintBody();
				this._scheduleMount('members');
			}
		}
	}
	editRole(pIDJoin) { this._editMemberJoin = Number(pIDJoin) || 0; this.repaintBody(); this._scheduleMount('members'); }
	cancelEditRole() { this._editMemberJoin = 0; this.repaintBody(); this._scheduleMount('members'); }
	async applyEditRole(pIDRole)
	{
		const join = this._editMemberJoin; if (!join) { return; }
		this._editMemberJoin = 0;
		await this.setMemberRole(join, Number(pIDRole) || 0);
	}
	async commitGrantDocument()
	{
		const sel = this._pendingDoc;
		if (!sel || !sel.id)
		{
			this._showNotice('documents', 'Pick a document to grant: choose its project and search by name, or type its document number.');
			return;
		}
		const role = this._pendingDocRole || 0;
		this._clearPicker('DocPicker');
		this._clearPicker('DocRolePicker');
		this._pendingDoc = null; this._pendingDocRole = 0;
		await this.grantDocument(sel.id, sel.type, role);
	}
	/**
	 * A project was picked (or cleared) on the Documents tab: name searches now run in it. A document picked from
	 * another project no longer applies.
	 * @param {any} pIDProject
	 */
	setGrantProject(pIDProject)
	{
		const tmpIDProject = Number(pIDProject) || 0;
		if (tmpIDProject === this._pendingProject)
		{
			return;
		}
		this._pendingProject = tmpIDProject;
		if (this._pendingDoc && this._pendingDoc.project && (this._pendingDoc.project !== tmpIDProject))
		{
			this._clearPicker('DocPicker');
			this._pendingDoc = null;
		}
	}
	async addMember(pIDUser, pIDRole)
	{
		const id = Number(pIDUser) || 0;
		if (!id)
		{
			return;
		}
		await this._write('members', 'add-member', () => this._postJSON('TeamUserJoin', { IDTeam: this.idTeam, IDUser: id, IDRole: Number(pIDRole) || 0, IDCustomer: this.idCustomer }));
	}
	async setMemberRole(pIDJoin, pIDRole)
	{
		const id = Number(pIDJoin) || 0;
		if (!id)
		{
			return;
		}
		await this._write('members', 'set-role', () => this._putJSON('TeamUserJoin', { IDTeamUserJoin: id, IDRole: Number(pIDRole) || 0 }));
	}
	async removeMember(pIDJoin)
	{
		const id = Number(pIDJoin) || 0;
		if (!id || this._memberWrite)
		{
			return;
		}
		await this._runMemberWrite({ Kind: 'remove', IDJoin: id }, () => this._write('members', 'remove-member', () => this._deleteJSON(`TeamUserJoin/${id}`)));
	}
	async grantDocument(pIDDocument, pDocumentType, pIDRole)
	{
		const id = Number(pIDDocument) || 0;
		if (!id)
		{
			return;
		}
		await this._write('documents', 'grant', () => this._postJSON('DocumentAccessJoin', { IDDocument: id, DocumentType: pDocumentType || '', IDTeam: this.idTeam, IDRole: Number(pIDRole) || 0, AccessKind: 'Owner', GrantRule: 'Config', IDCustomer: this.idCustomer }));
	}
	async revokeDocument(pIDJoin)
	{
		const id = Number(pIDJoin) || 0;
		if (!id)
		{
			return;
		}
		await this._write('documents', 'revoke', () => this._deleteJSON(`DocumentAccessJoin/${id}`));
	}
	async addLab(pIDLab, pIDRole)
	{
		const id = Number(pIDLab) || 0;
		if (!id || this._labAddPending)
		{
			return;
		}
		this._clearPicker('LabPicker');
		if (this._isLabLinked(id, pIDRole))
		{
			this._showNotice('labs', `${this._names.lab[id] || ('Lab ' + id)} is already linked to this team.`);
			return;
		}
		this._labAddPending = true;
		try
		{
			await this._write('labs', 'add-lab', () => this._postJSON('LabTeamJoin', { IDLab: id, IDTeam: this.idTeam, IDRole: Number(pIDRole) || 0, SupportKind: '', IDCustomer: this.idCustomer }));
		}
		finally
		{
			this._labAddPending = false;
		}
	}
	/**
	 * @param {number} pIDLab
	 * @param {number} [pIDRole] - 0 or absent: the whole-team link the Labs tab adds.
	 * @returns {boolean} Whether the loaded Labs tab already links this team to the lab with that role.
	 */
	_isLabLinked(pIDLab, pIDRole)
	{
		const tmpIDRole = Number(pIDRole) || 0;
		return (this._state('labs').Rows || []).some((r) => (Number(r.IDLab) === Number(pIDLab)) && ((Number(r.IDRole) || 0) === tmpIDRole));
	}
	async removeLab(pIDJoin)
	{
		const id = Number(pIDJoin) || 0;
		if (!id)
		{
			return;
		}
		await this._write('labs', 'remove-lab', () => this._deleteJSON(`LabTeamJoin/${id}`));
	}
	openTeam(pID) { const id = Number(pID) || 0; if (id) { try { window.location.hash = `#/Team/Workspace/${id}`; } catch (e) { /* */ } } }

	// =========================================================== tab bodies ===========================================================
	/**
	 * @param {string} pTab
	 * @returns {string} The tab's failure notice, or ''.
	 */
	_noticeHTML(pTab)
	{
		if (!this._notice || (this._notice.Tab !== pTab))
		{
			return '';
		}
		return `<div class="tw-error" role="alert"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this._notice.Message)}</div>`;
	}

	_overviewHTML()
	{
		const t = this._team || {}; const s = this._state('overview');
		const loading = this._loadingOrError('overview');
		const stats = loading ? '' : `<div class="tw-stats">
			<div class="tw-stat"><div class="tw-stat-v">${(s.Members || 0).toLocaleString()}</div><div class="tw-stat-l">Members</div></div>
			<div class="tw-stat"><div class="tw-stat-v">${(s.Documents || 0).toLocaleString()}</div><div class="tw-stat-l">Documents granted</div></div>
			<div class="tw-stat"><div class="tw-stat-v">${(s.Subteams || 0).toLocaleString()}</div><div class="tw-stat-l">Sub-teams</div></div>
		</div>`;
		return `${stats}
			<div class="tw-fields">
				<div><div class="tw-field-label">Name</div><div class="tw-field-value">${this._esc(t.Name || '—')}</div></div>
				<div><div class="tw-field-label">Parent team</div><div class="tw-field-value">${this._parentName ? this._esc(this._parentName) : '<span class="tw-muted">— top-level —</span>'}</div></div>
			</div>
			${t.Description ? `<div class="tw-sec-h">Description</div><div class="tw-field-value">${this._esc(t.Description)}</div>` : ''}
			<div class="tw-hint"><i class="fas fa-circle-info"></i> Members can open and edit the documents granted to this team, and reach those documents' projects, without being added to the projects.</div>`;
	}

	_membersHTML()
	{
		const tmpNotice = this._noticeHTML('members');
		const loading = this._loadingOrError('members');
		if (loading)
		{
			return tmpNotice + loading;
		}
		const P = this.providerName; const rows = this._state('members').Rows || [];
		const tmpWrite = this._memberWrite;
		const tmpBusy = tmpWrite ? ' disabled' : '';
		const tmpAddLabel = (tmpWrite && (tmpWrite.Kind === 'add')) ? '<i class="fas fa-circle-notch fa-spin"></i> Adding…' : '<i class="fas fa-plus"></i> Add to team';
		const hint = `<div class="tw-hint"><i class="fas fa-circle-info"></i> A role here only matters for documents granted to members in that role (Documents tab). It does not give the person that role's permissions.</div>`;
		const add = `${hint}<div class="tw-addbar">
			<div class="tw-f"><span class="tw-f-l">Add person</span><div id="TeamWorkspace-MemberPicker" class="tw-pick"></div></div>
			<div class="tw-f"><span class="tw-f-l">Role (optional)</span><div id="TeamWorkspace-MemberRolePicker" class="tw-pick"></div></div>
			<button class="tw-add-btn"${tmpBusy} onclick="_Pict.providers.${P}.commitAddMember()">${tmpAddLabel}</button>
		</div>`;
		const removeIcon = (r) => ((tmpWrite && (tmpWrite.Kind === 'remove') && (tmpWrite.IDJoin === r.IDTeamUserJoin)) ? 'fas fa-circle-notch fa-spin' : 'fas fa-user-minus');
		const roleCell = (r) =>
		{
			if (this._editMemberJoin === r.IDTeamUserJoin)
			{
				return `<div class="tw-rolecell"><div id="TeamWorkspace-EditRolePicker" class="tw-pick"></div><button class="tw-editrole" title="Cancel" onclick="_Pict.providers.${P}.cancelEditRole()"><i class="fas fa-xmark"></i></button></div>`;
			}
			return `<span class="tw-chip">${this._roleLabel(r.IDRole)}</span> <button class="tw-editrole" title="Change role" onclick="_Pict.providers.${P}.editRole(${r.IDTeamUserJoin})"><i class="fas fa-pen"></i></button>`;
		};
		const memberCell = (r) =>
		{
			const tmpLogin = this._names.login[r.IDUser];
			return `<span class="tw-name">${this._esc(this._names.user[r.IDUser] || ('#' + r.IDUser))}</span>${tmpLogin ? `<span class="tw-sub">${this._esc(tmpLogin)}</span>` : ''}`;
		};
		const body = rows.length ? `<table class="tw-table"><thead><tr><th>Member</th><th class="tw-col-role">Role in team</th><th></th></tr></thead><tbody>${rows.map((r) => `<tr>
			<td>${memberCell(r)}</td>
			<td>${roleCell(r)}</td>
			<td style="text-align:right"><button class="tw-x" title="Remove from team"${tmpBusy} onclick="_Pict.providers.${P}.removeMember(${r.IDTeamUserJoin})"><i class="${removeIcon(r)}"></i></button></td>
		</tr>`).join('')}</tbody></table>` : `<div class="tw-empty">No members yet — add someone above.</div>`;
		return tmpNotice + add + body;
	}

	_documentsHTML()
	{
		const tmpNotice = this._noticeHTML('documents');
		const loading = this._loadingOrError('documents');
		if (loading)
		{
			return tmpNotice + loading;
		}
		const P = this.providerName; const rows = this._state('documents').Rows || [];
		const add = `<div class="tw-addbar">
			<div class="tw-f"><span class="tw-f-l">Project</span><div id="TeamWorkspace-DocProjectPicker" class="tw-pick"></div></div>
			<div class="tw-f"><span class="tw-f-l">Document (name, or number)</span><div id="TeamWorkspace-DocPicker" class="tw-pick"></div></div>
			<div class="tw-f"><span class="tw-f-l">Only members in this role (optional)</span><div id="TeamWorkspace-DocRolePicker" class="tw-pick"></div></div>
			<button class="tw-add-btn" onclick="_Pict.providers.${P}.commitGrantDocument()"><i class="fas fa-plus"></i> Grant</button>
		</div>`;
		const body = rows.length ? `<table class="tw-table"><thead><tr><th>Document</th><th>Form type</th><th>Granted to</th><th></th></tr></thead><tbody>${rows.map((r) =>
		{
			const m = (this._docMeta || {})[r.IDDocument] || {};
			return `<tr>
			<td><span class="tw-name">${this._esc(m.Name || ('Document #' + r.IDDocument))}</span><span class="tw-sub">#${Number(r.IDDocument)}</span></td>
			<td><span class="tw-chip">${this._esc(this._formTypeLabel(r.DocumentType || m.DocumentType))}</span></td>
			<td>${r.IDRole ? `members in role ${this._roleLabel(r.IDRole)}` : '<span class="tw-muted">whole team</span>'}</td>
			<td style="text-align:right"><button class="tw-x" title="Remove this grant" onclick="_Pict.providers.${P}.revokeDocument(${r.IDDocumentAccessJoin})"><i class="fas fa-xmark"></i></button></td>
		</tr>`;
		}).join('')}</tbody></table>` : `<div class="tw-empty">No documents are granted to this team yet. Members can open and edit a granted document, and reach its project, without being added to the project.</div>`;
		return tmpNotice + add + body;
	}

	_projectsHTML()
	{
		const loading = this._loadingOrError('projects'); if (loading) { return loading; }
		const rows = this._state('projects').Rows || [];
		const hint = `<div class="tw-hint"><i class="fas fa-diagram-project"></i><span>The team reaches these projects because it is granted documents in them; its members are not added to the projects. To add a project, grant one of its documents on the <b>Documents</b> tab.</span></div>`;
		const body = rows.length ? `<table class="tw-table"><thead><tr><th>Project</th><th>Documents granted</th></tr></thead><tbody>${rows.map((r) => `<tr>
			<td><span class="tw-name">${this._esc(this._names.project[r.IDProject] || ('Project ' + r.IDProject))}</span></td>
			<td><span class="tw-chip">${r.DocCount}</span></td>
		</tr>`).join('')}</tbody></table>` : `<div class="tw-empty">The team reaches no projects yet. Grant it a document to reach that document's project.</div>`;
		return hint + body;
	}

	_inspectionsHTML()
	{
		const loading = this._loadingOrError('inspections'); if (loading) { return loading; }
		const rows = this._state('inspections').Rows || [];
		if (!rows.length)
		{
			return `<div class="tw-hint"><i class="fas fa-clipboard-check"></i><span>Inspections this team can reach because it is granted their documents. Assign the team to an inspection from the <b>Inspection workspace → Teams</b> tab.</span></div><div class="tw-empty">No inspections assigned to this team yet.</div>`;
		}
		const fmt = (v) => this._fmtDate(v) || '<span class="tw-muted">—</span>';
		const buckets = { upcoming: [], inprogress: [], completed: [] };
		rows.forEach((r) => { buckets[this._inspectionBucket(r)].push(r); });
		const order = (a, b) => (new Date(a.ScheduledStartDate || a.DeadlineDate || 0)).getTime() - (new Date(b.ScheduledStartDate || b.DeadlineDate || 0)).getTime();
		const section = (pTitle, pIcon, pList) => !pList.length ? '' : `<div class="tw-sec-h"><i class="${pIcon}"></i> ${pTitle} <span class="tw-chip">${pList.length}</span></div>
			<table class="tw-table"><thead><tr><th>Inspection</th><th>Type</th><th>Scheduled</th><th>Deadline</th><th></th></tr></thead><tbody>${pList.sort(order).map((r) => `<tr>
				<td><span class="tw-name">${this._esc(r.Name || ('Inspection ' + r.IDInspection))}</span></td>
				<td>${r.TypeDisplay ? `<span class="tw-chip">${this._esc(r.TypeDisplay)}</span>` : '<span class="tw-muted">—</span>'}</td>
				<td style="white-space:nowrap">${fmt(r.ScheduledStartDate)}</td>
				<td style="white-space:nowrap">${fmt(r.DeadlineDate)}</td>
				<td style="text-align:right"><a class="tw-link" href="#/Inspection/Workspace/${r.IDInspection}">Open <i class="fas fa-arrow-right"></i></a></td>
			</tr>`).join('')}</tbody></table>`;
		return `<div class="tw-hint"><i class="fas fa-clipboard-check"></i> The team reaches these inspections because it is granted their documents.</div>
			${section('Upcoming', 'fas fa-calendar-day', buckets.upcoming)}
			${section('In progress', 'fas fa-person-digging', buckets.inprogress)}
			${section('Completed', 'fas fa-circle-check', buckets.completed)}`;
	}

	_subteamsHTML()
	{
		const loading = this._loadingOrError('subteams'); if (loading) { return loading; }
		const rows = this._state('subteams').Rows || [];
		const body = rows.length ? `<table class="tw-table"><thead><tr><th>Sub-team</th><th>Description</th><th></th></tr></thead><tbody>${rows.map((r) => `<tr>
			<td><span class="tw-name">${this._esc(r.Name || ('Team ' + r.IDTeam))}</span></td>
			<td>${r.Description ? this._esc(r.Description) : '<span class="tw-muted">—</span>'}</td>
			<td style="text-align:right"><a class="tw-link" href="#/Team/Workspace/${r.IDTeam}">Open <i class="fas fa-arrow-right"></i></a></td>
		</tr>`).join('')}</tbody></table>` : `<div class="tw-empty">No sub-teams.</div>`;
		return `<div class="tw-hint"><i class="fas fa-circle-info"></i> Sub-teams are for organizing only: a document granted to this team reaches this team's own members, not the members of its sub-teams. Grant documents to each sub-team that needs them.</div>${body}`;
	}

	_labsHTML()
	{
		const tmpNotice = this._noticeHTML('labs');
		const loading = this._loadingOrError('labs');
		if (loading)
		{
			return tmpNotice + loading;
		}
		const P = this.providerName; const rows = this._state('labs').Rows || [];
		const hint = `<div class="tw-hint"><i class="fas fa-circle-info"></i> The team's members can open and work every test form for tests assigned to these labs, without being added to the project. Tests at a lab's child labs are not included: add each lab the team works. Removing a lab takes those forms away from the team.</div>`;
		const add = `${hint}<div class="tw-addbar"><div class="tw-f"><span class="tw-f-l">Add a lab</span><div id="TeamWorkspace-LabPicker" class="tw-pick"></div></div></div>`;
		const body = rows.length ? `<table class="tw-table"><thead><tr><th>Lab</th><th>Support kind</th><th>Role</th><th></th></tr></thead><tbody>${rows.map((r) => `<tr>
			<td><span class="tw-name">${this._esc(this._names.lab[r.IDLab] || ('Lab ' + r.IDLab))}</span></td>
			<td>${r.SupportKind ? this._esc(r.SupportKind) : '<span class="tw-muted">—</span>'}</td>
			<td>${r.IDRole ? `<span class="tw-chip">${this._roleLabel(r.IDRole)}</span>` : '<span class="tw-muted">—</span>'}</td>
			<td style="text-align:right"><button class="tw-x" title="Remove" onclick="_Pict.providers.${P}.removeLab(${r.IDLabTeamJoin})"><i class="fas fa-xmark"></i></button></td>
		</tr>`).join('')}</tbody></table>` : `<div class="tw-empty">This team staffs no labs.</div>`;
		return tmpNotice + add + body;
	}

	// =========================================================== pickers ===========================================================
	_scheduleMount(pTab) { try { setTimeout(() => this._mountPickers(pTab), 0); } catch (e) { /* */ } }
	_picker() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }
	_mountPickers(pTab)
	{
		const tmpPicker = this._picker(); const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const mount = (pHostID, pName, pDataProvider, pOnChange, pPlaceholder, pExtra) =>
		{
			if (tmpCA.getElement(`#${pHostID}`).length === 0)
			{
				return null;
			}
			const v = tmpPicker.createPicker(`${this.providerName}-${pName}`, Object.assign({
				DestinationAddress: `#${pHostID}`, ValueAddress: `AppData.${this.providerName}.${pName}`,
				Searchable: true, AllowClear: true, ClearLabel: `— ${pPlaceholder} —`, Placeholder: `${pPlaceholder}…`,
				DataProvider: pDataProvider, ResolveValue: () => Promise.resolve(null), OnChange: pOnChange,
			}, pExtra || {}));
			if (v && (typeof v.render === 'function'))
			{
				v.render();
			}
			return v;
		};
		if (pTab === 'members')
		{
			mount('TeamWorkspace-MemberPicker', 'MemberPicker', this._userDataProvider(), (val, rec) => { this._pendingUser = val ? { id: Number(val), rec: rec } : null; }, 'search people');
			mount('TeamWorkspace-MemberRolePicker', 'MemberRolePicker', this._roleDataProvider(), (val, rec) => { this._pendingRole = Number(val) || 0; this._cacheRoleName(val, rec); }, 'any role');
			if (this._editMemberJoin)
			{
				// Opens on the member's current role; clearing it makes them a plain member again.
				const fApply = (val, rec) =>
				{
					this._cacheRoleName(val, rec);
					this.applyEditRole(Number(val) || 0);
				};
				const tmpEdit = mount('TeamWorkspace-EditRolePicker', 'EditRolePicker', this._roleDataProvider(), fApply, 'member (no role)',
					{ Placeholder: 'member (no role)', ResolveValue: (pValue) => this._roleOption(pValue) });
				if (tmpEdit && (typeof tmpEdit.setValue === 'function'))
				{
					tmpEdit.setValue(this._editingMemberRole() || null);
				}
			}
		}
		else if (pTab === 'documents')
		{
			mount('TeamWorkspace-DocProjectPicker', 'DocProjectPicker', this._projectDataProvider(), (val) => this.setGrantProject(val), 'search projects');
			mount('TeamWorkspace-DocPicker', 'DocPicker', this._docDataProvider(), (val, rec) =>
			{
				const tmpRow = (rec && rec.Record) || {};
				this._pendingDoc = val ? { id: Number(val), type: tmpRow.DocumentType || '', project: Number(tmpRow.IDProject) || 0 } : null;
			}, 'name or number', { EmptyLabel: (pTerm) => libDocumentLookup.documentSearchEmptyLabel(pTerm, this._pendingProject) });
			mount('TeamWorkspace-DocRolePicker', 'DocRolePicker', this._roleDataProvider(), (val, rec) => { this._pendingDocRole = Number(val) || 0; this._cacheRoleName(val, rec); }, 'any role');
		}
		else if (pTab === 'labs') { mount('TeamWorkspace-LabPicker', 'LabPicker', this._labDataProvider(), (val) => { if (val) { this.addLab(val, 0); } }, 'search labs'); }
	}
	_cacheRoleName(pVal, pRec) { const id = Number(pVal) || 0; if (id && pRec && pRec.Record && pRec.Record.Name) { this._names.role[id] = pRec.Record.Name; } }
	_userDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._get(pURL), { Scope: 'FBV~Deleted~EQ~0', PageSize: 25 });
	}
	// Documents are found by number, or by name within the picked project (never a customer-wide name scan).
	_docDataProvider()
	{
		return libDocumentLookup.documentPickerDataProvider((pURL) => this._get(pURL), () => ({ IDCustomer: this.idCustomer, IDProject: this._pendingProject }));
	}
	_projectDataProvider()
	{
		return libDocumentLookup.projectPickerDataProvider((pURL) => this._get(pURL));
	}
	_labDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'Labs', IDField: 'IDLab', Include: (r) => !this._isLabLinked(r.IDLab, 0),
				ToOption: (r) => ({ Value: Number(r.IDLab), Text: r.Name || `Lab ${r.IDLab}`, Record: r }),
			});
	}
}

module.exports = TeamWorkspaceProvider;
module.exports.default_configuration = { ProviderIdentifier: 'TeamWorkspace' };
module.exports.writeFailureMessage = writeFailureMessage;
